import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const CHANNELS = ['WhatsApp','Phone Call','Email','Walk-in','Instagram DM','Facebook','Referral','Other'];
const PIPELINE_STAGES = [
  { key:'lead',      label:'New Lead',          color:'#8A8A8A', bg:'#F0F0F0' },
  { key:'quoted',    label:'Quoted',            color:'#1E4FAD', bg:'#EDF4FF' },
  { key:'negotiating',label:'Negotiating',      color:'#8B5E0A', bg:'#FFF3DC' },
  { key:'confirmed', label:'Order Confirmed',   color:'#2E6B3A', bg:'#E8F5EB' },
  { key:'customer',  label:'Repeat Customer',   color:'#556B2F', bg:'#F2EFE7' },
  { key:'inactive',  label:'Inactive',          color:'#8F2E24', bg:'#FDEAEA' },
];

const LEAD_STATUSES = [
  {
    key: 'new',
    label: 'New',
    color: '#555',
    bg: '#F0F0F0'
  },
  {
    key: 'contacted',
    label: 'Contacted',
    color: '#1E4FAD',
    bg: '#EDF4FF'
  },
  {
    key: 'qualified',
    label: 'Qualified',
    color: '#8B5E0A',
    bg: '#FFF3DC'
  },
  {
    key: 'proposal',
    label: 'Proposal',
    color: '#556B2F',
    bg: '#F2EFE7'
  },
  {
    key: 'converted',
    label: 'Converted',
    color: '#2E6B3A',
    bg: '#E8F5EB'
  },
  {
    key: 'lost',
    label: 'Lost',
    color: '#8F2E24',
    bg: '#FDEAEA'
  }
];

const getLeadStatusBadge = status => {
  const selected =
    LEAD_STATUSES.find(
      item => item.key === status
    ) || LEAD_STATUSES[0];

  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        background: selected.bg,
        color: selected.color
      }}
    >
      {selected.label}
    </span>
  );
};

const getStageBadge = (stage) => {
  const s = PIPELINE_STAGES.find(p => p.key===stage) || PIPELINE_STAGES[4];
  return <span style={{display:'inline-flex',padding:'3px 10px',borderRadius:20,fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',background:s.bg,color:s.color}}>{s.label}</span>;
};

export default function CRM() {
  const { user } = useAuth();
  const [view, setView] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [leadInteractions, setLeadInteractions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [showLeadInteraction, setShowLeadInteraction] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [showLog, setShowLog] = useState(null);
  const [editInteraction, setEditInteraction] = useState(null);
  const [search, setSearch] = useState('');

  const loadDashboard = () => {
    setLoading(true);
    get('/crm/dashboard').then(setDashboard).finally(() => setLoading(false));
  };

  const loadInteractions = () => {
    Promise.all([
      get('/crm/interactions'),
      get('/crm/lead-interactions')
    ]).then(([customerData, leadData]) => {
      setInteractions(
        customerData.data || []
      );

      setLeadInteractions(
        (leadData.data || []).map(
          interaction => ({
            ...interaction,
            customer_name:
              interaction.lead_name,
            customer_phone:
              interaction.lead_phone,
            subject_type: 'lead'
          })
        )
      );
    });
  };

  const loadLeads = () => {
    get('/crm/leads').then(
      data => setLeads(data.data || [])
    );
  };

  const loadLeadDetail = id => {
    get('/crm/leads/' + id).then(
      setLeadDetail
    );
  };

  const loadCustomers = () => {
    get('/customers').then(d => setCustomers(d.data||[]));
  };

  const loadCustomerDetail = (id) => {
    get('/crm/customers/'+id).then(setCustomerDetail);
  };

  useEffect(() => {
    loadDashboard();
    loadCustomers();
    loadLeads();
    loadInteractions();
  }, []);

  const handleSelectLead = lead => {
    setSelectedLead(lead);
    loadLeadDetail(lead.id);
    setView('lead');
  };

  const handleSelectCustomer = (c) => {
    setSelectedCustomer(c);
    loadCustomerDetail(c.id);
    setView('customer');
  };

  const handleMarkDone = async (id) => {
    try { await put('/crm/interactions/'+id+'/done', {}); loadDashboard(); loadInteractions(); }
    catch (err) { alert(err.message); }
  };

  const handleDeleteInteraction = async (id) => {
    if (!window.confirm('Delete this interaction?')) return;
    try { await del('/crm/interactions/'+id); loadDashboard(); loadInteractions(); if (customerDetail) loadCustomerDetail(customerDetail.customer.id); }
    catch (err) { alert(err.message); }
  };

  const filteredCustomers = search
    ? customers.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone||'').includes(search))
    : customers;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">CRM</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Customer relationships and follow-ups</p>
        </div>
        <div className="flex-center gap-sm">
          {['dashboard','interactions','leads','customers'].map(v => (
            <button key={v} className={'btn btn-sm '+(view===v?'btn-primary':'btn-outline')}
              onClick={() => setView(v)} style={{textTransform:'capitalize'}}>
              {v==='dashboard'
                ? 'Dashboard'
                : v==='interactions'
                  ? 'All Interactions'
                  : v==='leads'
                    ? 'Leads'
                    : 'Customers'}
            </button>
          ))}
          {view==='customer' && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setView('customers');
                setSelectedCustomer(null);
                setCustomerDetail(null);
              }}
            >
              Back
            </button>
          )}

          {view==='lead' && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setView('leads');
                setSelectedLead(null);
                setLeadDetail(null);
              }}
            >
              Back
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() =>
              setShowLog({
                type: 'lead'
              })
            }
          >
            + Log New Lead Interaction
          </button>
        </div>
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {view==='dashboard' && dashboard && <DashboardView dashboard={dashboard} onMarkDone={handleMarkDone} onSelectCustomer={handleSelectCustomer} customers={customers} />}
          {view==='interactions' && (
            <InteractionsView
              interactions={[
                ...interactions,
                ...leadInteractions
              ].sort((a, b) =>
                String(b.date).localeCompare(
                  String(a.date)
                )
              )}
              onDelete={handleDeleteInteraction}
              onEdit={setEditInteraction}
              onMarkDone={handleMarkDone}
            />
          )}
          {view==='leads' && (
            <LeadsView
              leads={leads}
              onSelect={handleSelectLead}
            />
          )}

          {view==='lead' && leadDetail && (
            <LeadDetailView
              detail={leadDetail}
              onAddInteraction={() =>
                setShowLeadInteraction(true)
              }
              onRefresh={() => {
                loadLeadDetail(
                  leadDetail.lead.id
                );
                loadLeads();
                loadDashboard();
                loadInteractions();
              }}
            />
          )}

          {view==='customers' && (
            <CustomersView customers={filteredCustomers} search={search} setSearch={setSearch} onSelect={handleSelectCustomer} />
          )}
          {view==='customer' && customerDetail && (
            <CustomerDetailView
              detail={customerDetail}
              onLogInteraction={() =>
                setShowLog({
                  type: 'customer',
                  customer: selectedCustomer
                })
              }
              onMarkDone={handleMarkDone}
              onDelete={handleDeleteInteraction}
              onEdit={setEditInteraction}
              onRefresh={() => loadCustomerDetail(customerDetail.customer.id)}
            />
          )}
        </>
      )}

      {showLog?.type === 'lead' && (
        <LeadInteractionModal
          onClose={() => setShowLog(null)}
          onSaved={() => {
            setShowLog(null);
            loadDashboard();
            loadInteractions();
          }}
        />
      )}

      {showLog?.type === 'customer' && (
        <LogInteractionModal
          customers={customers}
          defaultCustomer={
            showLog.customer ||
            selectedCustomer
          }
          onClose={() => setShowLog(null)}
          onSaved={() => {
            setShowLog(null);
            loadDashboard();
            loadInteractions();

            if (customerDetail) {
              loadCustomerDetail(
                customerDetail.customer.id
              );
            }
          }}
        />
      )}

      {showLeadInteraction && leadDetail && (
        <RepeatLeadInteractionModal
          lead={leadDetail.lead}
          onClose={() =>
            setShowLeadInteraction(false)
          }
          onSaved={() => {
            setShowLeadInteraction(false);
            loadLeadDetail(
              leadDetail.lead.id
            );
            loadLeads();
            loadDashboard();
            loadInteractions();
          }}
        />
      )}

      {editInteraction && (
        <EditInteractionModal
          interaction={editInteraction}
          onClose={() => setEditInteraction(null)}
          onSaved={() => {
            setEditInteraction(null);
            loadDashboard();
            loadInteractions();
            if (customerDetail) loadCustomerDetail(customerDetail.customer.id);
          }} />
      )}
    </div>
  );
}

function DashboardView({ dashboard, onMarkDone, onSelectCustomer, customers }) {
  const {
    overdue_follow_ups,
    today_follow_ups,
    upcoming_follow_ups,
    at_risk_customers,
    at_risk_leads = [],
    pipeline,
    recent_interactions
  } = dashboard;

  return (
    <div>
      {/* Pipeline summary */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        {pipeline.map(p => (
          <div key={p.pipeline_stage} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 16px',minWidth:120,textAlign:'center',boxShadow:'var(--shadow)'}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',fontWeight:700,color:'#556B2F'}}>{p.count}</div>
            <div style={{fontSize:10,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginTop:2}}>
              {PIPELINE_STAGES.find(s=>s.key===p.pipeline_stage)?.label||p.pipeline_stage}
            </div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Overdue follow-ups */}
        <div className="card">
          <div className="card-hdr" style={{borderLeft:'3px solid var(--unpaid)'}}>
            <span className="card-title" style={{color:'var(--unpaid)'}}>Overdue Follow-ups ({overdue_follow_ups.length})</span>
          </div>
          {overdue_follow_ups.length===0
            ? <div style={{padding:'16px 20px',fontSize:12,color:'#8A8A8A'}}>No overdue follow-ups</div>
            : overdue_follow_ups.map(f => (
              <div key={f.id} style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: 13,
                      color: '#3B2A21',
                      cursor:
                        f.subject_type === 'customer'
                          ? 'pointer'
                          : 'default'
                    }}
                    onClick={() => {
                      if (
                        f.subject_type === 'customer'
                      ) {
                        onSelectCustomer({
                          id: f.customer_id,
                          name: f.customer_name
                        });
                      }
                    }}
                  >
                    {f.customer_name}
                    {f.subject_type === 'lead' && (
                      <span
                        className="badge b-grey"
                        style={{
                          fontSize: 8,
                          marginLeft: 6
                        }}
                      >
                        LEAD
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{f.summary.substring(0,60)}{f.summary.length>60?'...':''}</div>
                  <div style={{fontSize:10,color:'var(--unpaid)',marginTop:2,fontWeight:600}}>Due: {f.follow_up_date}</div>
                </div>
                <button className="btn btn-gold btn-sm" style={{marginLeft:8,flexShrink:0}} onClick={() => onMarkDone(f.id)}>Done</button>
              </div>
            ))
          }
        </div>

        {/* Today's follow-ups */}
        <div className="card">
          <div className="card-hdr" style={{borderLeft:'3px solid var(--gold)'}}>
            <span className="card-title">Today's Follow-ups ({today_follow_ups.length})</span>
          </div>
          {today_follow_ups.length===0
            ? <div style={{padding:'16px 20px',fontSize:12,color:'#8A8A8A'}}>No follow-ups today</div>
            : today_follow_ups.map(f => (
              <div key={f.id} style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:500,fontSize:13,color:'#3B2A21'}}>{f.customer_name}</div>
                  <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{f.summary.substring(0,60)}{f.summary.length>60?'...':''}</div>
                  <div style={{fontSize:10,color:'var(--part)',marginTop:2}}>{f.channel}</div>
                </div>
                <button className="btn btn-gold btn-sm" style={{marginLeft:8,flexShrink:0}} onClick={() => onMarkDone(f.id)}>Done</button>
              </div>
            ))
          }
        </div>

        {/* Upcoming follow-ups */}
        <div className="card">
          <div className="card-hdr" style={{borderLeft:'3px solid var(--paid)'}}>
            <span className="card-title">Upcoming Follow-ups</span>
          </div>
          {upcoming_follow_ups.length===0
            ? <div style={{padding:'16px 20px',fontSize:12,color:'#8A8A8A'}}>No upcoming follow-ups</div>
            : upcoming_follow_ups.slice(0,5).map(f => (
              <div key={f.id} style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontWeight:500,fontSize:13}}>{f.customer_name}</div>
                  <div style={{fontSize:11,color:'var(--paid)',fontWeight:600}}>{f.follow_up_date}</div>
                </div>
                <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{f.summary.substring(0,60)}{f.summary.length>60?'...':''}</div>
              </div>
            ))
          }
        </div>

        {/* At risk customers */}
        <div className="card">
          <div className="card-hdr" style={{borderLeft:'3px solid var(--part)'}}>
            <span className="card-title">At Risk — No Contact 30+ Days</span>
          </div>
          {at_risk_customers.length===0
            ? <div style={{padding:'16px 20px',fontSize:12,color:'#8A8A8A'}}>All customers contacted recently</div>
            : at_risk_customers.map(c => (
              <div key={c.id} style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}}
                onClick={() => onSelectCustomer(c)}>
                <div>
                  <div style={{fontWeight:500,fontSize:13}}>{c.name}</div>
                  <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{c.phone||'No phone'}</div>
                </div>
                <div style={{fontSize:11,color:'var(--part)',fontWeight:600}}>{c.days_since} days ago</div>
              </div>
            ))
          }
        </div>
      </div>

      {at_risk_leads.length > 0 && (
        <div
          className="card"
          style={{marginTop:16}}
        >
          <div
            className="card-hdr"
            style={{
              borderLeft:
                '3px solid var(--part)'
            }}
          >
            <span className="card-title">
              Leads With No Contact for 30+ Days
            </span>
          </div>
      
          {at_risk_leads.map(lead => (
            <div
              key={lead.id}
              style={{
                padding: '10px 16px',
                borderBottom:
                  '1px solid var(--border-light)',
                display: 'flex',
                justifyContent:
                  'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13
                  }}
                >
                  {lead.name}
                </div>
      
                <div
                  style={{
                    fontSize: 11,
                    color: '#8A8A8A',
                    marginTop: 2
                  }}
                >
                  {lead.phone ||
                    lead.email ||
                    lead.social_handle ||
                    'No contact information'}
                </div>
      
                {lead.assigned_to_name && (
                  <div
                    style={{
                      fontSize: 10,
                      color: '#8A8A8A',
                      marginTop: 2
                    }}
                  >
                    Owner: {lead.assigned_to_name}
                  </div>
                )}
              </div>
      
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--part)',
                  fontWeight: 600
                }}
              >
                {lead.days_since} days ago
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Recent interactions */}
      {recent_interactions.length > 0 && (
        <div className="card" style={{marginTop:16}}>
          <div className="card-hdr"><span className="card-title">Recent Interactions</span></div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'var(--cream)'}}>
              <th style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
              <th style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Lead / Customer</th>
              <th style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Channel</th>
              <th style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Summary</th>
              <th style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>By</th>
            </tr></thead>
            <tbody>
              {recent_interactions.map(i => (
                <tr key={i.id}>
                  <td style={{padding:'9px 16px',borderBottom:'1px solid var(--border-light)'}}>{i.date}</td>
                  <td style={{padding:'9px 16px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{i.customer_name}</td>
                  <td style={{padding:'9px 16px',borderBottom:'1px solid var(--border-light)'}}><span className="badge b-grey" style={{fontSize:9}}>{i.channel}</span></td>
                  <td style={{padding:'9px 16px',borderBottom:'1px solid var(--border-light)',color:'#555',maxWidth:300}}>{i.summary.substring(0,80)}{i.summary.length>80?'...':''}</td>
                  <td style={{padding:'9px 16px',borderBottom:'1px solid var(--border-light)',color:'#8A8A8A',fontSize:12}}>{i.created_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InteractionsView({ interactions, onDelete, onEdit, onMarkDone }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? interactions.filter(i => i.customer_name?.toLowerCase().includes(search.toLowerCase()) || i.summary?.toLowerCase().includes(search.toLowerCase()))
    : interactions;

  return (
    <div>
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search by lead, customer or summary..." value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
      </div>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th><th>Lead / Customer</th><th>Channel</th><th>Summary</th>
              <th>Follow-up</th><th>By</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length===0
              ? <tr><td colSpan="7"><div className="empty-state"><h3>No interactions yet</h3></div></td></tr>
              : filtered.map(i => (
                <tr key={i.id}>
                  <td>{i.date}</td>
                  <td style={{fontWeight:500}}>{i.customer_name}</td>
                  <td><span className="badge b-grey" style={{fontSize:9}}>{i.channel}</span></td>
                  <td style={{color:'#555',maxWidth:250,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{i.summary}</td>
                  <td>
                    {i.follow_up_date ? (
                      <div>
                        <div style={{fontSize:12,color:i.follow_up_done?'var(--paid)':new Date(i.follow_up_date)<new Date()?'var(--unpaid)':'var(--ink-mid)',fontWeight:500}}>{i.follow_up_date}</div>
                        {i.follow_up_done && <span style={{fontSize:9,color:'var(--paid)'}}>Done</span>}
                        {!i.follow_up_done && <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 6px'}} onClick={()=>onMarkDone(i.id)}>Mark Done</button>}
                      </div>
                    ) : <span style={{color:'#8A8A8A',fontSize:12}}>None</span>}
                  </td>
                  <td style={{fontSize:12,color:'#8A8A8A'}}>{i.created_by_name}</td>
                  <td>
                    <div className="flex-center gap-sm">
                      <button className="btn btn-ghost btn-sm" onClick={()=>onEdit(i)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={()=>onDelete(i.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomersView({ customers, search, setSearch, onSelect }) {
  return (
    <div>
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search customers..." value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
        {customers.map(c => (
          <div key={c.id} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,padding:'14px 16px',cursor:'pointer',boxShadow:'var(--shadow)'}}
            onClick={()=>onSelect(c)}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{c.name}</div>
              {getStageBadge(c.pipeline_stage||'customer')}
            </div>
            <div style={{fontSize:12,color:'#555'}}>{c.phone||'No phone'}</div>
            {c.email && <div style={{fontSize:12,color:'#8A8A8A',marginTop:2}}>{c.email}</div>}
            {c.last_interaction && (
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:6}}>Last contact: {c.last_interaction}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerDetailView({ detail, onLogInteraction, onMarkDone, onDelete, onEdit, onRefresh }) {
  const { customer, interactions, invoices, job_orders, stats } = detail;
  const [editingStage, setEditingStage] = useState(false);
  const [stage, setStage] = useState(customer.pipeline_stage||'customer');
  const [preferences, setPreferences] = useState(customer.preferences||'');
  const [saving, setSaving] = useState(false);

  const saveCustomerInfo = async () => {
    setSaving(true);
    try {
      await put('/crm/customers/'+customer.id, { pipeline_stage:stage, preferences });
      setEditingStage(false);
      onRefresh();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  return (
    <div>
      {/* Customer header */}
      <div style={{background:'#556B2F',borderRadius:10,padding:'16px 22px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:600,color:'#A8893D'}}>{customer.name}</div>
          <div style={{fontSize:12,color:'rgba(242,239,231,.6)',marginTop:3}}>
            {customer.phone||''} {customer.email?'| '+customer.email:''}
          </div>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {getStageBadge(customer.pipeline_stage||'customer')}
          <button className="btn btn-gold btn-sm" onClick={onLogInteraction}>+ Log Interaction</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
          <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Total Orders</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:'#3B2A21'}}>{stats.total_orders}</div>
        </div>
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
          <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Total Revenue</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:'#556B2F'}}>{fmt(stats.total_revenue)}</div>
        </div>
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
          <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Total Paid</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:'var(--paid)'}}>{fmt(stats.total_paid)}</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',gap:16}}>
        {/* Left column - interactions + invoices */}
        <div>
          {/* Interaction timeline */}
          <div className="card" style={{marginBottom:16}}>
            <div className="card-hdr"><span className="card-title">Interaction History</span></div>
            {interactions.length===0
              ? <div style={{padding:'16px 20px',fontSize:12,color:'#8A8A8A'}}>No interactions logged yet</div>
              : <div style={{position:'relative',padding:'12px 20px',paddingLeft:36}}>
                  <div style={{position:'absolute',left:20,top:0,bottom:0,width:2,background:'var(--border-light)'}}></div>
                  {interactions.map(i => (
                    <div key={i.id} style={{position:'relative',marginBottom:16}}>
                      <div style={{position:'absolute',left:-20,top:4,width:8,height:8,borderRadius:'50%',background:'#556B2F',border:'2px solid #fff',boxShadow:'0 0 0 1px var(--border-light)'}}></div>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:4}}>
                            <span className="badge b-grey" style={{fontSize:9}}>{i.channel}</span>
                            <span style={{fontSize:11,color:'#8A8A8A'}}>{i.date}</span>
                            <span style={{fontSize:11,color:'#8A8A8A'}}>by {i.created_by_name}</span>
                          </div>
                          <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.5}}>{i.summary}</div>
                          {i.outcome && <div style={{fontSize:12,color:'#556B2F',marginTop:4,fontStyle:'italic'}}>Outcome: {i.outcome}</div>}
                          {i.follow_up_date && (
                            <div style={{marginTop:4,display:'flex',alignItems:'center',gap:6}}>
                              <span style={{fontSize:11,color:i.follow_up_done?'var(--paid)':new Date(i.follow_up_date)<new Date()?'var(--unpaid)':'var(--ink-light)',fontWeight:500}}>
                                Follow-up: {i.follow_up_date} {i.follow_up_done?'✓':''}
                              </span>
                              {!i.follow_up_done && <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 6px'}} onClick={()=>onMarkDone(i.id)}>Mark Done</button>}
                            </div>
                          )}
                        </div>
                        <div className="flex-center gap-sm" style={{marginLeft:8,flexShrink:0}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>onEdit(i)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={()=>onDelete(i.id)}>Del</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>

          {/* Invoice history */}
          {invoices.length > 0 && (
            <div className="card">
              <div className="card-hdr"><span className="card-title">Invoice History</span></div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'var(--cream)'}}>
                  <th style={{padding:'7px 12px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Invoice</th>
                  <th style={{padding:'7px 12px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
                  <th style={{padding:'7px 12px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Total</th>
                  <th style={{padding:'7px 12px',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Status</th>
                </tr></thead>
                <tbody>
                  {invoices.map(i => (
                    <tr key={i.invoice_number}>
                      <td style={{padding:'7px 12px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{i.invoice_number}</td>
                      <td style={{padding:'7px 12px',borderBottom:'1px solid var(--border-light)'}}>{i.date}</td>
                      <td style={{padding:'7px 12px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(i.total)}</td>
                      <td style={{padding:'7px 12px',borderBottom:'1px solid var(--border-light)'}}>
                        <span className={'badge b-'+(i.status==='paid'?'paid':i.status==='part_paid'?'part':'unpaid')} style={{fontSize:9}}>{i.status.replace('_',' ')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right column - pipeline + preferences + job orders */}
        <div>
          <div className="card" style={{marginBottom:16}}>
            <div className="card-hdr">
              <span className="card-title">Pipeline & Preferences</span>
              {!editingStage && <button className="btn btn-ghost btn-sm" onClick={()=>setEditingStage(true)}>Edit</button>}
            </div>
            <div style={{padding:'14px 16px'}}>
              {editingStage ? (
                <div>
                  <div className="form-group">
                    <label className="form-label">Pipeline Stage</label>
                    <select className="form-control" value={stage} onChange={e=>setStage(e.target.value)}>
                      {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Preferences / Notes</label>
                    <textarea className="form-control" rows={3} placeholder="e.g. Always wants natural jute, prefers WhatsApp, budget-conscious..."
                      value={preferences} onChange={e=>setPreferences(e.target.value)} />
                  </div>
                  <div className="flex-center gap-sm">
                    <button className="btn btn-outline btn-sm" onClick={()=>setEditingStage(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={saveCustomerInfo} disabled={saving}>
                      {saving?'Saving...':'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{marginBottom:10}}>{getStageBadge(customer.pipeline_stage||'customer')}</div>
                  {customer.preferences
                    ? <div style={{fontSize:13,color:'#555',lineHeight:1.6}}>{customer.preferences}</div>
                    : <div style={{fontSize:12,color:'#8A8A8A',fontStyle:'italic'}}>No preferences noted yet</div>
                  }
                </div>
              )}
            </div>
          </div>

          {/* Job orders */}
          {job_orders.length > 0 && (
            <div className="card">
              <div className="card-hdr"><span className="card-title">Job Orders</span></div>
              {job_orders.map(j => (
                <div key={j.job_number} style={{padding:'10px 14px',borderBottom:'1px solid var(--border-light)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div style={{fontWeight:500,fontSize:13}}>{j.job_number}</div>
                    <span className="badge b-grey" style={{fontSize:9}}>{j.status.replace('_',' ')}</span>
                  </div>
                  <div style={{fontSize:12,color:'#555',marginTop:2}}>{j.product_description||'—'}</div>
                  <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{j.date}{j.delivery_date?' → '+j.delivery_date:''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadsView({
  leads,
  onSelect
}) {
  const [search, setSearch] =
    useState('');

  const [status, setStatus] =
    useState('active');

  const filtered = leads.filter(lead => {
    const searchText =
      search.trim().toLowerCase();

    const matchesSearch =
      !searchText ||
      [
        lead.name,
        lead.phone,
        lead.email,
        lead.organisation,
        lead.social_handle,
        lead.lead_ref
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(searchText)
      );

    const matchesStatus =
      status === 'all' ||
      (
        status === 'active' &&
        !['converted', 'lost'].includes(
          lead.status
        )
      ) ||
      lead.status === status;

    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div
        className="search-bar"
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: 8
        }}
      >
        <input
          placeholder="Search leads by name, phone, organisation or handle..."
          value={search}
          onChange={event =>
            setSearch(event.target.value)
          }
          style={{flex:1}}
        />

        <select
          className="form-control"
          value={status}
          onChange={event =>
            setStatus(event.target.value)
          }
          style={{maxWidth:180}}
        >
          <option value="active">
            Active Leads
          </option>
          <option value="all">
            All Leads
          </option>
          {LEAD_STATUSES.map(item => (
            <option
              key={item.key}
              value={item.key}
            >
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fill,minmax(270px,1fr))',
          gap: 12
        }}
      >
        {filtered.map(lead => (
          <div
            key={lead.id}
            className="card"
            onClick={() => onSelect(lead)}
            style={{
              padding: '14px 16px',
              cursor: 'pointer'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                alignItems: 'flex-start',
                gap: 10
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#3B2A21'
                  }}
                >
                  {lead.name}
                </div>

                <div
                  style={{
                    fontSize: 10,
                    color: '#8A8A8A',
                    marginTop: 2
                  }}
                >
                  {lead.lead_ref}
                </div>
              </div>

              {getLeadStatusBadge(
                lead.status
              )}
            </div>

            {lead.organisation && (
              <div
                style={{
                  fontSize: 12,
                  color: '#555',
                  marginTop: 8
                }}
              >
                {lead.organisation}
              </div>
            )}

            <div
              style={{
                fontSize: 12,
                color: '#555',
                marginTop: 6
              }}
            >
              {lead.phone ||
                lead.email ||
                lead.social_handle}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                marginTop: 10,
                fontSize: 10,
                color: '#8A8A8A'
              }}
            >
              <span>
                Owner: {lead.assigned_to_name}
              </span>

              <span>
                {lead.interaction_count || 0}
                {' '}
                interaction(s)
              </span>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <h3>No matching leads</h3>
        </div>
      )}
    </div>
  );
}

function LeadDetailView({
  detail,
  onAddInteraction,
  onRefresh
}) {
  const {
    lead,
    interactions,
    assignments
  } = detail;

  const [changingStatus, setChangingStatus] =
    useState(false);

  const [error, setError] =
    useState('');

  const closed =
    ['converted', 'lost'].includes(
      lead.status
    );

  const updateStatus = async status => {
    let lostReason = '';

    if (status === 'lost') {
      lostReason =
        window.prompt(
          'Why was this lead lost?',
          ''
        );

      if (lostReason === null) return;

      lostReason = lostReason.trim();

      if (!lostReason) {
        setError(
          'A reason is required when marking a lead as lost.'
        );
        return;
      }

      if (
        !window.confirm(
          'Close this lead as lost? It cannot be reopened without manager approval.'
        )
      ) {
        return;
      }
    }

    setChangingStatus(true);
    setError('');

    try {
      await put(
        '/crm/leads/' +
          lead.id +
          '/status',
        {
          status,
          lost_reason: lostReason
        }
      );

      onRefresh();
    } catch (err) {
      setError(
        err.message ||
          'The lead status could not be updated.'
      );
    } finally {
      setChangingStatus(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="alert alert-error mb-md">
          {error}
        </div>
      )}

      <div
        className="card"
        style={{marginBottom:16}}
      >
        <div className="card-hdr">
          <div>
            <div className="card-title">
              {lead.name}
            </div>

            <div
              style={{
                fontSize: 10,
                color: '#8A8A8A',
                marginTop: 3
              }}
            >
              {lead.lead_ref}
            </div>
          </div>

          <div className="flex-center gap-sm">
            {getLeadStatusBadge(lead.status)}

            {!closed && (
              <button
                className="btn btn-primary btn-sm"
                onClick={onAddInteraction}
              >
                + Add Interaction
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '14px 16px',
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14
          }}
        >
          <LeadField
            label="Phone"
            value={lead.phone}
          />

          <LeadField
            label="Email"
            value={lead.email}
          />

          <LeadField
            label="Social Handle"
            value={lead.social_handle}
          />

          <LeadField
            label="Organisation"
            value={lead.organisation}
          />

          <LeadField
            label="Lead Source"
            value={lead.lead_source}
          />

          <LeadField
            label="Preferred Channel"
            value={lead.preferred_channel}
          />

          <LeadField
            label="Owner"
            value={lead.assigned_to_name}
          />

          <LeadField
            label="Created By"
            value={lead.created_by_name}
          />
        </div>
      </div>

      {!closed && (
        <div
          className="card"
          style={{marginBottom:16}}
        >
          <div className="card-hdr">
            <span className="card-title">
              Sales Stage
            </span>
          </div>

          <div
            style={{
              padding: '14px 16px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8
            }}
          >
            {[
              'contacted',
              'qualified',
              'proposal'
            ].map(status => (
              <button
                key={status}
                className={
                  lead.status === status
                    ? 'btn btn-primary btn-sm'
                    : 'btn btn-outline btn-sm'
                }
                disabled={
                  changingStatus ||
                  lead.status === status
                }
                onClick={() =>
                  updateStatus(status)
                }
              >
                {
                  LEAD_STATUSES.find(
                    item =>
                      item.key === status
                  )?.label
                }
              </button>
            ))}

            <button
              className="btn btn-danger btn-sm"
              disabled={changingStatus}
              onClick={() =>
                updateStatus('lost')
              }
            >
              Mark as Lost
            </button>
          </div>
        </div>
      )}

      {lead.status === 'lost' &&
       lead.lost_reason && (
        <div
          className="alert alert-error mb-md"
        >
          Lost reason: {lead.lost_reason}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(0,2fr) minmax(260px,1fr)',
          gap: 16
        }}
      >
        <div className="card">
          <div className="card-hdr">
            <span className="card-title">
              Interaction History
            </span>
          </div>

          {interactions.length === 0 ? (
            <div className="empty-state">
              <h3>No interactions</h3>
            </div>
          ) : interactions.map(
            interaction => (
              <div
                key={interaction.id}
                style={{
                  padding: '12px 16px',
                  borderBottom:
                    '1px solid var(--border-light)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent:
                      'space-between',
                    gap: 10
                  }}
                >
                  <strong
                    style={{fontSize:13}}
                  >
                    {interaction.channel}
                  </strong>

                  <span
                    style={{
                      fontSize: 11,
                      color: '#8A8A8A'
                    }}
                  >
                    {interaction.date}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: '#555',
                    lineHeight: 1.5,
                    marginTop: 6
                  }}
                >
                  {interaction.summary}
                </div>

                {interaction.outcome && (
                  <div
                    style={{
                      fontSize: 11,
                      marginTop: 5
                    }}
                  >
                    Outcome: {interaction.outcome}
                  </div>
                )}

                {interaction.follow_up_date && (
                  <div
                    style={{
                      fontSize: 11,
                      marginTop: 4,
                      color:
                        interaction.follow_up_done
                          ? 'var(--paid)'
                          : 'var(--part)'
                    }}
                  >
                    Follow-up:
                    {' '}
                    {interaction.follow_up_date}
                    {interaction.follow_up_done
                      ? ' • Done'
                      : ''}
                  </div>
                )}

                <div
                  style={{
                    fontSize: 10,
                    color: '#8A8A8A',
                    marginTop: 5
                  }}
                >
                  Logged by:
                  {' '}
                  {interaction.created_by_name}
                </div>
              </div>
            )
          )}
        </div>

        <div className="card">
          <div className="card-hdr">
            <span className="card-title">
              Ownership History
            </span>
          </div>

          {assignments.map(assignment => (
            <div
              key={assignment.id}
              style={{
                padding: '12px 14px',
                borderBottom:
                  '1px solid var(--border-light)'
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500
                }}
              >
                {assignment.new_owner_name}
              </div>

              <div
                style={{
                  fontSize: 10,
                  color: '#8A8A8A',
                  marginTop: 3
                }}
              >
                {assignment.reason}
              </div>

              <div
                style={{
                  fontSize: 10,
                  color: '#8A8A8A',
                  marginTop: 3
                }}
              >
                Assigned by:
                {' '}
                {assignment.assigned_by_name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LeadField({
  label,
  value
}) {
  return (
    <div>
      <div className="form-label">
        {label}
      </div>

      <div
        style={{
          fontSize: 13,
          color: value
            ? '#3B2A21'
            : '#8A8A8A'
        }}
      >
        {value || 'Not provided'}
      </div>
    </div>
  );
}

function RepeatLeadInteractionModal({
  lead,
  onClose,
  onSaved
}) {
  const [form, setForm] =
    useState({
      date:
        new Date()
          .toISOString()
          .split('T')[0],
      channel:
        lead.preferred_channel ||
        'WhatsApp',
      summary: '',
      outcome: '',
      follow_up_date: '',
      next_action: ''
    });

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const submit = async () => {
    if (
      !form.date ||
      !form.channel ||
      !form.summary.trim()
    ) {
      setError(
        'Date, channel and summary are required.'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      await post(
        '/crm/leads/' +
          lead.id +
          '/interactions',
        form
      );

      onSaved();
    } catch (err) {
      setError(
        err.message ||
          'The interaction could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{maxWidth:560}}
      >
        <div className="modal-hdr">
          <span className="modal-title">
            Add Interaction: {lead.name}
          </span>

          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={saving}
          >
            X
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error mb-md">
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Date *
              </label>

              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={event =>
                  setForm({
                    ...form,
                    date: event.target.value
                  })
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Channel *
              </label>

              <select
                className="form-control"
                value={form.channel}
                onChange={event =>
                  setForm({
                    ...form,
                    channel:
                      event.target.value
                  })
                }
              >
                {CHANNELS.map(channel => (
                  <option
                    key={channel}
                    value={channel}
                  >
                    {channel}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Summary *
            </label>

            <textarea
              className="form-control"
              rows={4}
              value={form.summary}
              onChange={event =>
                setForm({
                  ...form,
                  summary: event.target.value
                })
              }
              placeholder="What was discussed?"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Outcome
            </label>

            <input
              className="form-control"
              value={form.outcome}
              onChange={event =>
                setForm({
                  ...form,
                  outcome: event.target.value
                })
              }
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Follow-up Date
              </label>

              <input
                type="date"
                className="form-control"
                min={form.date}
                value={form.follow_up_date}
                onChange={event =>
                  setForm({
                    ...form,
                    follow_up_date:
                      event.target.value
                  })
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Next Action
              </label>

              <input
                className="form-control"
                value={form.next_action}
                onChange={event =>
                  setForm({
                    ...form,
                    next_action:
                      event.target.value
                  })
                }
              />
            </div>
          </div>
        </div>

        <div className="modal-ftr">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving
              ? 'Saving...'
              : 'Save Interaction'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadInteractionModal({
  onClose,
  onSaved
}) {
  const [form, setForm] = useState({
    lead_name: '',
    phone: '',
    email: '',
    organisation: '',
    social_handle: '',
    lead_source: '',
    preferred_channel: 'WhatsApp',
    date:
      new Date()
        .toISOString()
        .split('T')[0],
    channel: 'WhatsApp',
    summary: '',
    outcome: '',
    follow_up_date: '',
    next_action: ''
  });

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const update = (field, value) => {
    setForm(previous => ({
      ...previous,
      [field]: value
    }));

    setError('');
  };

  const submit = async () => {
    if (!form.lead_name.trim()) {
      setError('Lead name is required.');
      return;
    }

    if (
      !form.phone.trim() &&
      !form.email.trim() &&
      !form.social_handle.trim()
    ) {
      setError(
        'Enter a phone number, email address or social-media handle.'
      );
      return;
    }

    if (
      !form.date ||
      !form.channel ||
      !form.summary.trim()
    ) {
      setError(
        'Date, channel and interaction summary are required.'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      await post(
        '/crm/lead-interactions',
        form
      );

      onSaved();
    } catch (err) {
      setError(
        err.message ||
          'The lead interaction could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth: 680,
          width: '95vw'
        }}
      >
        <div className="modal-hdr">
          <span className="modal-title">
            Log New Lead Interaction
          </span>

          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={saving}
          >
            X
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error mb-md">
              {error}
            </div>
          )}

          <div
            className="alert mb-md"
            style={{
              background: '#F2EFE7',
              border: '1px solid #D8CFBC',
              color: '#3B2A21'
            }}
          >
            This creates a new sales lead and
            assigns ownership to you. Existing
            customers must be updated from their
            customer record instead.
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Lead Name *
              </label>

              <input
                className="form-control"
                value={form.lead_name}
                onChange={event =>
                  update(
                    'lead_name',
                    event.target.value
                  )
                }
                placeholder="Individual or contact name"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Organisation
              </label>

              <input
                className="form-control"
                value={form.organisation}
                onChange={event =>
                  update(
                    'organisation',
                    event.target.value
                  )
                }
                placeholder="Business or organisation"
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Phone Number
              </label>

              <input
                type="tel"
                className="form-control"
                value={form.phone}
                onChange={event =>
                  update(
                    'phone',
                    event.target.value
                  )
                }
                placeholder="e.g. 08028708436"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email Address
              </label>

              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={event =>
                  update(
                    'email',
                    event.target.value
                  )
                }
                placeholder="Optional"
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Social-media Handle
              </label>

              <input
                className="form-control"
                value={form.social_handle}
                onChange={event =>
                  update(
                    'social_handle',
                    event.target.value
                  )
                }
                placeholder="@username"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Lead Source
              </label>

              <input
                className="form-control"
                value={form.lead_source}
                onChange={event =>
                  update(
                    'lead_source',
                    event.target.value
                  )
                }
                placeholder="Instagram, referral, walk-in..."
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Interaction Date *
              </label>

              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={event =>
                  update(
                    'date',
                    event.target.value
                  )
                }
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Channel *
              </label>

              <select
                className="form-control"
                value={form.channel}
                onChange={event => {
                  update(
                    'channel',
                    event.target.value
                  );

                  update(
                    'preferred_channel',
                    event.target.value
                  );
                }}
                disabled={saving}
              >
                {CHANNELS.map(channel => (
                  <option
                    key={channel}
                    value={channel}
                  >
                    {channel}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Interaction Summary *
            </label>

            <textarea
              className="form-control"
              rows={4}
              value={form.summary}
              onChange={event =>
                update(
                  'summary',
                  event.target.value
                )
              }
              placeholder="What does the lead need, quantity, product, event date, budget and specifications?"
              disabled={saving}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Outcome
              </label>

              <input
                className="form-control"
                value={form.outcome}
                onChange={event =>
                  update(
                    'outcome',
                    event.target.value
                  )
                }
                placeholder="Quote requested, sample requested..."
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Follow-up Date
              </label>

              <input
                type="date"
                className="form-control"
                value={form.follow_up_date}
                min={form.date}
                onChange={event =>
                  update(
                    'follow_up_date',
                    event.target.value
                  )
                }
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Next Action
            </label>

            <input
              className="form-control"
              value={form.next_action}
              onChange={event =>
                update(
                  'next_action',
                  event.target.value
                )
              }
              placeholder="Send quotation, call again, prepare mockup..."
              disabled={saving}
            />
          </div>
        </div>

        <div className="modal-ftr">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving
              ? 'Saving...'
              : 'Create Lead and Log Interaction'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LogInteractionModal({ customers, defaultCustomer, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_id: defaultCustomer?.id||'',
    date: new Date().toISOString().split('T')[0],
    channel: 'WhatsApp',
    summary: '',
    outcome: '',
    follow_up_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.customer_id || !form.summary) { setError('Customer and summary required'); return; }
    setSaving(true);
    try {
      await post('/crm/interactions', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:520}}>
        <div className="modal-hdr">
          <span className="modal-title">Log Interaction</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Customer *</label>
            <select className="form-control" value={form.customer_id} onChange={e=>setForm({...form,customer_id:e.target.value})}>
              <option value="">Select customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-control" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Channel *</label>
              <select className="form-control" value={form.channel} onChange={e=>setForm({...form,channel:e.target.value})}>
                {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Summary *</label>
            <textarea className="form-control" rows={3}
              placeholder="What was discussed? e.g. Customer called about 200 jute bags for wedding, requested proforma, budget around 150k..."
              value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <input className="form-control" placeholder="e.g. Proforma sent, Sample requested, Order confirmed..."
              value={form.outcome} onChange={e=>setForm({...form,outcome:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Follow-up Date</label>
            <input type="date" className="form-control" value={form.follow_up_date}
              onChange={e=>setForm({...form,follow_up_date:e.target.value})} />
            <div className="form-hint">Set a date to follow up with this customer</div>
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':'Log Interaction'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditInteractionModal({ interaction, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: interaction.date||'',
    channel: interaction.channel||'WhatsApp',
    summary: interaction.summary||'',
    outcome: interaction.outcome||'',
    follow_up_date: interaction.follow_up_date||'',
    follow_up_done: interaction.follow_up_done||false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.summary) { setError('Summary required'); return; }
    setSaving(true);
    try {
      await put('/crm/interactions/'+interaction.id, form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:520}}>
        <div className="modal-hdr">
          <span className="modal-title">Edit Interaction</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Channel</label>
              <select className="form-control" value={form.channel} onChange={e=>setForm({...form,channel:e.target.value})}>
                {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Summary *</label>
            <textarea className="form-control" rows={3} value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <input className="form-control" value={form.outcome||''} onChange={e=>setForm({...form,outcome:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Follow-up Date</label>
            <input type="date" className="form-control" value={form.follow_up_date||''} onChange={e=>setForm({...form,follow_up_date:e.target.value})} />
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:8}}>
            <input type="checkbox" id="fu_done" checked={form.follow_up_done}
              onChange={e=>setForm({...form,follow_up_done:e.target.checked})} />
            <label htmlFor="fu_done" style={{fontSize:13,color:'var(--ink-mid)'}}>Follow-up completed</label>
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
